import { devLogger } from "@certa/common/utils/devLogger";
import {
  getViewport,
  isViewportInitialized
} from "../../pixi-components/layers/viewport/viewport.layer";
import { getInstances, getRootNode } from "../renderingEngine/renderer";

const PANNING_BOUNDARY_LIMIT = 1000;

// Private global variable to store the root node bounds
const _globalRootNodeBounds = {
  width: 0,
  height: 0
};

/**
 * This function essentially gets the layout values of the root node, by getting the instances and the root node.
 * Returns undefined if the root node is not found or the viewport is not initialized.
 */
const getRootNodeLayoutValues = () => {
  const instances = getInstances();
  const rootNode = getRootNode();

  if (!rootNode) {
    return;
  }

  if (!isViewportInitialized()) {
    return;
  }

  const rootComponent = instances[rootNode.key];

  /*
   TODO: Use getLayoutValues
   We are intentionally using the yogaNode here, as layout values for root node is not updated in the layoutCalculator.
  */
  const { width, height } = rootComponent.yogaNode.getComputedLayout();
  return { width, height };
};

/**
 * This function essentially stores the layout values of the root node in a _globalRootNodeBounds
 * Returns the newly stored value from _globalRootNodeBounds
 */
const getRootNodeBounds = () => {
  const rootNodeLayoutValues = getRootNodeLayoutValues();

  if (!rootNodeLayoutValues) {
    return;
  }

  const { width, height } = rootNodeLayoutValues;

  _globalRootNodeBounds.width = width;
  _globalRootNodeBounds.height = height;

  return _globalRootNodeBounds;
};

/**
 * This function essentially cleans up the stored node bounds, by setting the width and height to 0.
 * This is used in unrenderer.ts
 */
export const cleanUpRootNodeBounds = () => {
  _globalRootNodeBounds.width = 0;
  _globalRootNodeBounds.height = 0;
};

/**
 * This function essentially checks if the root node bounds have changed,
 * by comparing the current bounds with the previously stored bounds.
 */
export const isRootNodeBoundsChanged = () => {
  const rootNodeLayoutValues = getRootNodeLayoutValues();

  if (!rootNodeLayoutValues) {
    return false;
  }

  const { width, height } = rootNodeLayoutValues;
  return (
    _globalRootNodeBounds.width !== width ||
    _globalRootNodeBounds.height !== height
  );
};

export const updateWorldBoundaries = () => {
  const rootBounds = getRootNodeBounds();

  if (!rootBounds) {
    return;
  }

  const { width, height } = rootBounds;

  const viewport = getViewport();

  // Fixed:Top and Left boundaries, as we only progress forward/downward in the canvas
  const clampTop = PANNING_BOUNDARY_LIMIT;
  const clampLeft = PANNING_BOUNDARY_LIMIT;

  // Dynamic World boundaries based on the rootComponent width and height
  const clampRight = width + PANNING_BOUNDARY_LIMIT;
  const clampBottom = height + PANNING_BOUNDARY_LIMIT;

  viewport.clamp({
    left: -clampLeft,
    right: clampRight,
    top: -clampTop,
    bottom: clampBottom
  });

  devLogger.log("🌎 World Boundary Updated");
};
